/**
 * Topography commands (topo round — docs/MODELER.md "TOPOGRAPHY element"): the only
 * write path for the topo element's `points` / `breaklines` / `modifiers` data
 * params. Every mutation clones the whole array and replaces it ATOMICALLY in one
 * transaction (the Path2D convention — one command = one undo step); entries carry
 * stable ids so handles and the settings modal address them across edits.
 *
 * The GRADING model is deliberately total: a modifier is data on the stack, never a
 * boolean against the surface, so no command here can produce an invalid surface —
 * "errors impossible by construction" is the acceptance criterion. Stack order IS
 * the precedence (later wins where footprints overlap); `moveModifier` reorders it.
 *
 * Elevations in and out of this file are PROJECT-RELATIVE feet (types.ts#TopoPoint) —
 * the same unit as every other element's z. Real-world entry/display is a UI-layer
 * concern only (the site datum, exactly like Levels) — see datums.ts#toRealFt/
 * toProjectFt and the frontend call sites that convert at the point of typing/showing
 * a number, never here.
 */
import { nanoid } from 'nanoid'
import type { ElementId, PhaseId, Pt2, TopoBreakline, TopoModifier, TopoPoint } from '../types'
import { getElement, maps, transact, type ModelDoc } from '../doc'

const mint = () => nanoid(12)

function readArray<T>(model: ModelDoc, elementId: ElementId, key: string): T[] {
  const el = getElement(model, elementId)
  if (!el || el.category !== 'topo') return []
  const v = el.params[key]
  return Array.isArray(v) ? (JSON.parse(JSON.stringify(v)) as T[]) : []
}

function writeParam(model: ModelDoc, elementId: ElementId, key: string, value: unknown): boolean {
  const em = maps(model).elements.get(elementId)
  if (!em) return false
  transact(model, () => {
    const pm = em.get('params') as { set(k: string, v: unknown): void }
    pm.set(key, value)
  })
  return true
}

// ---------- spot points ----------

export function addTopoPoint(model: ModelDoc, elementId: ElementId, pt: Pt2, zFt: number): string | null {
  const points = readArray<TopoPoint>(model, elementId, 'points')
  const id = mint()
  points.push({ id, pt: [pt[0], pt[1]], zFt })
  return writeParam(model, elementId, 'points', points) ? id : null
}

export function updateTopoPoint(
  model: ModelDoc,
  elementId: ElementId,
  pointId: string,
  patch: { pt?: Pt2; zFt?: number },
): boolean {
  const points = readArray<TopoPoint>(model, elementId, 'points')
  const p = points.find((x) => x.id === pointId)
  if (!p) return false
  if (patch.pt !== undefined) p.pt = [patch.pt[0], patch.pt[1]]
  if (patch.zFt !== undefined) p.zFt = patch.zFt
  return writeParam(model, elementId, 'points', points)
}

/**
 * Batch point patch — ONE transaction = one undo step, the multi-point selection's
 * write path (dragging a shared elevation delta, setting several points to one
 * elevation). Unknown ids are skipped; returns false only when nothing matched.
 */
export function updateTopoPoints(
  model: ModelDoc,
  elementId: ElementId,
  updates: { id: string; pt?: Pt2; zFt?: number }[],
): boolean {
  const points = readArray<TopoPoint>(model, elementId, 'points')
  let any = false
  for (const u of updates) {
    const p = points.find((x) => x.id === u.id)
    if (!p) continue
    if (u.pt !== undefined) p.pt = [u.pt[0], u.pt[1]]
    if (u.zFt !== undefined) p.zFt = u.zFt
    any = true
  }
  if (!any) return false
  return writeParam(model, elementId, 'points', points)
}

/**
 * Delete a point AND retire it from every breakline in the same transaction — a
 * breakline left with fewer than 2 points is dropped entirely (never a dangling ref).
 */
export function deleteTopoPoint(model: ModelDoc, elementId: ElementId, pointId: string): boolean {
  return deleteTopoPoints(model, elementId, [pointId])
}

/** Batch delete with the same breakline cascade — one transaction = one undo step. */
export function deleteTopoPoints(model: ModelDoc, elementId: ElementId, pointIds: string[]): boolean {
  const gone = new Set(pointIds)
  const points = readArray<TopoPoint>(model, elementId, 'points')
  const next = points.filter((x) => !gone.has(x.id))
  if (next.length === points.length) return false
  const breaklines = readArray<TopoBreakline>(model, elementId, 'breaklines')
    .map((b) => ({ ...b, ptIds: b.ptIds.filter((id) => !gone.has(id)) }))
    .filter((b) => b.ptIds.length >= 2)
  const em = maps(model).elements.get(elementId)
  if (!em) return false
  transact(model, () => {
    const pm = em.get('params') as { set(k: string, v: unknown): void }
    pm.set('points', next)
    pm.set('breaklines', breaklines)
  })
  return true
}

// ---------- breaklines ----------

export function addTopoBreakline(model: ModelDoc, elementId: ElementId, ptIds: string[]): string | null {
  if (ptIds.length < 2) return null
  const points = readArray<TopoPoint>(model, elementId, 'points')
  const known = new Set(points.map((p) => p.id))
  if (!ptIds.every((id) => known.has(id))) return null
  const breaklines = readArray<TopoBreakline>(model, elementId, 'breaklines')
  const id = mint()
  breaklines.push({ id, ptIds: [...ptIds] })
  return writeParam(model, elementId, 'breaklines', breaklines) ? id : null
}

export function deleteTopoBreakline(model: ModelDoc, elementId: ElementId, breaklineId: string): boolean {
  const breaklines = readArray<TopoBreakline>(model, elementId, 'breaklines')
  const next = breaklines.filter((b) => b.id !== breaklineId)
  if (next.length === breaklines.length) return false
  return writeParam(model, elementId, 'breaklines', next)
}

// ---------- the modifier stack ----------

export function addTopoModifier(
  model: ModelDoc,
  elementId: ElementId,
  input: Omit<TopoModifier, 'id'>,
): string | null {
  const modifiers = readArray<TopoModifier>(model, elementId, 'modifiers')
  const id = mint()
  const mod: TopoModifier = { ...JSON.parse(JSON.stringify(input)) as Omit<TopoModifier, 'id'>, id }
  modifiers.push(mod)
  return writeParam(model, elementId, 'modifiers', modifiers) ? id : null
}

/**
 * Manual-grading round — the PHASE-AWARE elevation drag's write path: upsert GRADE
 * POINT modifiers, one transaction = one undo step. An entry lands on the existing
 * grade modifier at the same spot in the same phase (re-dragging refines the grade
 * instead of stacking duplicates); otherwise it appends to the END of the stack
 * (later wins — the newest grading intent governs). Same-phase means literal
 * equality, untagged (`phaseCreated` absent) matching untagged.
 */
export function upsertTopoGradePoints(
  model: ModelDoc,
  elementId: ElementId,
  entries: { pt: Pt2; elevFt: number; slopeH?: number; phaseCreated?: PhaseId }[],
): boolean {
  if (!entries.length) return false
  const EPS = 1e-6
  const modifiers = readArray<TopoModifier>(model, elementId, 'modifiers')
  for (const e of entries) {
    const existing = modifiers.find((m) =>
      m.kind === 'grade' && m.pt
      && Math.hypot(m.pt[0] - e.pt[0], m.pt[1] - e.pt[1]) < EPS
      && (m.phaseCreated ?? null) === (e.phaseCreated ?? null))
    if (existing) {
      existing.elevFt = e.elevFt
      if (e.slopeH !== undefined) existing.slopeH = e.slopeH
    } else {
      modifiers.push({
        id: mint(), kind: 'grade', pt: [e.pt[0], e.pt[1]], elevFt: e.elevFt,
        ...(e.slopeH !== undefined ? { slopeH: e.slopeH } : {}),
        ...(e.phaseCreated ? { phaseCreated: e.phaseCreated } : {}),
      })
    }
  }
  return writeParam(model, elementId, 'modifiers', modifiers)
}

/**
 * Remove every modifier of `kind` referencing `refElementId` — the un-grade /
 * un-retain toggle's write path (Matt's report: the button only ever ADDED, so
 * repeated clicks stacked duplicates; this clears them all in ONE undo step).
 */
export function removeTopoModifiersReferencing(
  model: ModelDoc,
  elementId: ElementId,
  kind: TopoModifier['kind'],
  refElementId: ElementId,
): boolean {
  const modifiers = readArray<TopoModifier>(model, elementId, 'modifiers')
  const next = modifiers.filter((m) => !(m.kind === kind && m.refElementId === refElementId))
  if (next.length === modifiers.length) return false
  return writeParam(model, elementId, 'modifiers', next)
}

/** Patch-merge one modifier; `phaseCreated: null` clears it (always applied). */
export function updateTopoModifier(
  model: ModelDoc,
  elementId: ElementId,
  modifierId: string,
  patch: Partial<Omit<TopoModifier, 'id' | 'kind' | 'phaseCreated'>> & { phaseCreated?: PhaseId | null },
): boolean {
  const modifiers = readArray<TopoModifier>(model, elementId, 'modifiers')
  const m = modifiers.find((x) => x.id === modifierId)
  if (!m) return false
  const rec = m as unknown as Record<string, unknown>
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) continue
    if (v === null) delete rec[k]
    else rec[k] = JSON.parse(JSON.stringify(v))
  }
  return writeParam(model, elementId, 'modifiers', modifiers)
}

/** Move one vertex of a drawn modifier footprint (handle drags). */
export function moveTopoModifierVertex(
  model: ModelDoc,
  elementId: ElementId,
  modifierId: string,
  vertexIndex: number,
  pt: Pt2,
): boolean {
  const modifiers = readArray<TopoModifier>(model, elementId, 'modifiers')
  const m = modifiers.find((x) => x.id === modifierId)
  if (!m || !m.poly || vertexIndex < 0 || vertexIndex >= m.poly.length) return false
  m.poly[vertexIndex] = [pt[0], pt[1]]
  return writeParam(model, elementId, 'modifiers', modifiers)
}

export function deleteTopoModifier(model: ModelDoc, elementId: ElementId, modifierId: string): boolean {
  const modifiers = readArray<TopoModifier>(model, elementId, 'modifiers')
  const next = modifiers.filter((m) => m.id !== modifierId)
  if (next.length === modifiers.length) return false
  return writeParam(model, elementId, 'modifiers', next)
}

/** Reorder the stack: move a modifier one slot up (-1) or down (+1). Order IS precedence. */
export function moveTopoModifier(model: ModelDoc, elementId: ElementId, modifierId: string, dir: -1 | 1): boolean {
  const modifiers = readArray<TopoModifier>(model, elementId, 'modifiers')
  const i = modifiers.findIndex((m) => m.id === modifierId)
  const j = i + dir
  if (i < 0 || j < 0 || j >= modifiers.length) return false
  const [m] = modifiers.splice(i, 1)
  modifiers.splice(j, 0, m)
  return writeParam(model, elementId, 'modifiers', modifiers)
}

// ---------- reads ----------

export function topoPoints(model: ModelDoc, elementId: ElementId): TopoPoint[] {
  return readArray<TopoPoint>(model, elementId, 'points')
}
export function topoBreaklines(model: ModelDoc, elementId: ElementId): TopoBreakline[] {
  return readArray<TopoBreakline>(model, elementId, 'breaklines')
}
export function topoModifiers(model: ModelDoc, elementId: ElementId): TopoModifier[] {
  return readArray<TopoModifier>(model, elementId, 'modifiers')
}
